<template>
  <modal
    ref="modal"
    modal-title="Prepare reimbursement files for printing"
    modal-class="generate-completion-certificates-modal"
  >
    <template #modal-content>
      <template v-if="isWaiting">
        <div v-if="filesList.length > 0">
          <h6>Please select files to print</h6>
          <div class="ps-4">
            <div
              v-for="file of filesList"
              :key="file.id"
              class="form-check"
            >
              <input
                :id="`file-${file.id}`"
                v-model="file.selected"
                type="checkbox"
                class="form-check-input"
              >
              <label
                class="form-check-label mb-0"
                :for="`file-${file.id}`"
              >
                {{ file.name }}
              </label>
            </div>
          </div>
        </div>
        <p
          v-else
          class="text-center mt-4"
        >
          You are about to prepare reimbursement files for printing<br>({{ participantIds.length }} participants)
        </p>

        <div class="text-center mt-4 mb-4">
          <button
            class="btn btn-primary"
            @click.prevent="generatePdf"
          >
            Prepare
          </button>
        </div>
      </template>
      <loading-indicator
        v-else-if="isRunning"
        :message="statusMessage"
        :vertical="true"
        additional-css-class="mt-4"
      />
      <template v-else>
        <div
          v-for="(error, errorIdx) of errors"
          :key="errorIdx"
          class="alert alert-danger text-center mt-3"
        >
          {{ error }}
        </div>
        <div
          v-if="showPdfLink"
          class="mt-4 text-center"
        >
          PDF for printing has been generated <br>
          <a
            :href="pdfUrl ?? undefined"
            class="btn btn-success mt-3 mb-3"
            target="_blank"
          >
            Open document
          </a>
        </div>
      </template>
    </template>
    <template #modal-footer>
      <button
        type="button"
        class="btn btn-sm btn-secondary"
        data-bs-dismiss="modal"
      >
        Close
      </button>
    </template>
  </modal>
</template>
<script setup lang="ts">
import {computed, ref, useTemplateRef} from 'vue'

import LoadingIndicator from './loading-indicator.vue'
import Modal from './modal.vue'

import {getValueStrNullable, isNonEmptyString} from '../js-utils'
import type {GridID} from './types'

enum Status {
  waiting = 0,
  running = 1,
  finished = 2
}

export interface PrintFile {
  id: string;
  name: string;
  selected: boolean;
}

interface GeneratePdfResponse {
  status_url?: string | null;
}

interface CheckStatusResponse {
  errors?: unknown;
  finished?: boolean;
  message?: unknown;
  url?: unknown;
}

const modal = useTemplateRef<InstanceType<typeof Modal>>('modal')

const generateUrl = ref<string | null>(null)
const participantIds = ref<GridID[]>([])
const status = ref(Status.waiting)
const statusMessage = ref('')
const statusUrl = ref<string | null>(null)
const pdfUrl = ref<string | null>(null)
const errors = ref<string[]>([])
const filesList = ref<PrintFile[]>([])
const participantIdsKey = ref<string | null>(null)

const isWaiting = computed((): boolean => status.value === Status.waiting)

const isRunning = computed((): boolean => status.value === Status.running)

const showPdfLink = computed((): boolean => isNonEmptyString(pdfUrl.value))

const checkStatus = (): void => {
  if (isNonEmptyString(statusUrl.value)) {
    modal.value?.performRequest<CheckStatusResponse>(statusUrl.value, null, {withLoadingIndicator: false}, (isSuccess, data) => {
      if (isSuccess) {
        if (data?.finished) {
          status.value = Status.finished
          errors.value = Array.isArray(data.errors) ? data.errors : []
          if (isNonEmptyString(data.url)) {
            pdfUrl.value = data.url
          }
        } else {
          if (isNonEmptyString(data?.message)) {
            statusMessage.value = data.message
          }

          window.setTimeout(() => {
            checkStatus()
          }, 1000)
        }
      } else {
        status.value = Status.finished
      }
    })
  }
}

const generatePdf = (): void => {
  modal.value?.hideError()

  const fileIds = []
  if (filesList.value.length > 0) {
    for (const file of filesList.value) {
      if (file.selected) {
        fileIds.push(file.id)
      }
    }
    if (fileIds.length == 0) {
      alert('Please select at least one file')
      return
    }
  }

  if (isNonEmptyString(generateUrl.value)) {
    const data: Record<string, GridID[] | string[]> = {
      file_ids: fileIds
    }
    data[participantIdsKey.value || 'participant_ids'] = participantIds.value

    modal.value?.performRequest<GeneratePdfResponse>(
      generateUrl.value,
      data,
      {withLoadingIndicator: false},
      (isSuccess, data) => {
        if (isSuccess) {
          status.value = Status.running
          statusUrl.value = getValueStrNullable(data?.status_url || null)
          if (isNonEmptyString(statusUrl.value)) {
            checkStatus()
          }
        } else {
          status.value = Status.finished
        }
      }
    )
  }
}

const showModal = (convertUrl: string, ids: GridID[], files?: PrintFile[], idsKey?: string): void => {
  generateUrl.value = convertUrl
  participantIds.value = ids
  participantIdsKey.value = idsKey || null
  filesList.value = []
  if (Array.isArray(files)) {
    filesList.value = files
  }

  errors.value = []

  statusMessage.value = 'Initializing ...'

  statusUrl.value = null
  status.value = Status.waiting

  pdfUrl.value = null

  modal.value?.hideLoadingIndicator()
  modal.value?.showModal()
}

defineExpose({
  showModal,
})
</script>
